// import * as React from "react"
// import { Slot } from "@radix-ui/react-slot"
// import { cva, type VariantProps } from "class-variance-authority"

// import { cn } from "@/lib/utils"

// const buttonVariants = cva(
//   "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
//   {
//     variants: {
//       variant: {
//         default: "bg-primary text-primary-foreground hover:bg-primary/90",
//         destructive:
//           "bg-destructive text-destructive-foreground hover:bg-destructive/90",
//         outline:
//           "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
//         secondary:
//           "bg-secondary text-secondary-foreground hover:bg-secondary/80",
//         ghost: "hover:bg-accent hover:text-accent-foreground",
//         link: "text-primary underline-offset-4 hover:underline",
//       },
//       size: {
//         default: "h-10 px-4 py-2",
//         sm: "h-8 rounded-lg px-5 text-sm",
//         xs: "h-5 rounded-md  text-xs [&_svg]:size-3.5 px-3 py-1",
//         lg: "h-11 rounded-md px-8",
//         icon: "h-10 w-10",
//       },
//     },
//     defaultVariants: {
//       variant: "default",
//       size: "default",
//     },
//   }
// )

// export interface ButtonProps
//   extends React.ButtonHTMLAttributes<HTMLButtonElement>,
//   VariantProps<typeof buttonVariants> {
//   asChild?: boolean
// }

// const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
//   ({ className, variant, size, asChild = false, ...props }, ref) => {
//     const Comp = asChild ? Slot : "button"
//     return (
//       <Comp
//         className={cn(buttonVariants({ variant, size, className }))}
//         ref={ref}
//         {...props}
//       />
//     )
//   }
// )
// Button.displayName = "Button"

// export { Button, buttonVariants }

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// STRIPE-STYLED BUTTON DESIGN SYSTEM
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md transition-all select-none active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none [&_svg]:size-4",
  {
    variants: {
      variant: {
        // 🔵 Primary (Stripe Blue)
        default:
          "bg-blue-600 text-white font-medium hover:bg-blue-700 shadow-sm",

        // ❤️ Destructive
        destructive:
          "bg-red-600 text-white font-medium hover:bg-red-700",

        // 🌫 Outline – clean & neutral
        outline:
          "border border-gray-300 bg-white text-gray-800 font-medium hover:bg-gray-50",

        // 🩶 Secondary – subtle like Stripe
        secondary:
          "bg-gray-100 text-gray-900 font-normal hover:bg-gray-200 border border-transparent",

        // 👻 Ghost – minimal button
        ghost:
          "text-gray-700 font-normal hover:bg-gray-100",

        // 🔗 Link button
        link: "text-blue-600 font-normal underline-offset-4 hover:underline",
      },

      size: {
        // ⭐ Default Stripe-style wide button
        default: "h-10 px-6 text-sm",

        // Smaller action button
        sm: "h-8 px-4 text-sm rounded-md",

        // Extra small – for tags, small icons, etc.
        xs: "h-6 px-3 text-xs rounded-md [&_svg]:size-3",

        // Large CTA (Checkout / Submit)
        lg: "h-12 px-7 text-base rounded-md",

        // Icon-only button
        icon: "h-10 w-10",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }
